// /resources/js/app/modules/v2/V2DataService.js
import { apiClient } from './apiClient.js';

/**
 * @class V2DataService
 * This class acts as a dedicated data layer for the V2 application. Its sole
 * responsibility is to abstract away all API communication. This keeps the
 * main controller clean and makes it easy to change API endpoints or logic
 * in one central place.
 */
class V2DataService {
    /**
     * Fetches the definitions for all available streams (e.g., "Trending", "Early Dips").
     * @returns {Promise<Array>} - A promise that resolves to an array of stream definition objects.
     */
    async fetchStreamDefinitions() {
        try {
            const response = await apiClient.get('/api/v2/streams/definitions');
            return response.data;
        } catch (error) {
            console.error('[V2DataService] Failed to fetch stream definitions:', error);
            throw error;
        }
    }

    /**
     * Fetches a paginated and sorted list of signals for a specific stream.
     * @param {string} slug - The unique identifier for the stream (e.g., "trending").
     * @param {number} page - The page number to fetch.
     * @param {string} sort - The field to sort by.
     * @param {string} dir - The sort direction ('asc' or 'desc').
     * @param {boolean} bustCache - If true, adds a parameter to force a fresh response from the server.
     * @param {boolean} showUnlockedOnly - NEW: If true, adds a parameter to request only unlocked signals.
     * @returns {Promise<object>} - A promise that resolves to the API's pagination object.
     */
    async fetchStream(slug, page = 1, sort = 'call_timestamp', dir = 'desc', bustCache = false, showUnlockedOnly = false) {
        try {
            // The endpoint is determined by the stream slug. "my-signals" has a dedicated, authenticated route.
            const endpoint = slug === 'my-signals' ? '/api/v2/stream/my-signals' : `/api/v2/stream/${slug}`;
            const params = new URLSearchParams({ page, sort, dir });
            
            // This parameter tells the backend to bypass its own cache layers, ensuring the freshest data.
            if (bustCache) {
                params.set('bust_cache', 'true');
            }

            // NEW: Add the user-specific filter parameter to the request if the user has enabled the filter.
            if (showUnlockedOnly) {
                params.set('show', 'unlocked');
            }
            console.log(`[V2DataService] Fetching stream '${slug}'. API Params:`, params.toString());
            
            const response = await apiClient.get(endpoint, { params });
            return response.data;
        } catch (error) {
            console.error(`[V2DataService] Failed to fetch stream '${slug}':`, error);
            throw error;
        }
    }

    /**
     * Polls the stream status endpoint to check for new signals.
     * @returns {Promise<object|null>} - The full status response, including the version token and new signal counts.
     */
    async fetchStatus() {
        try {
            const response = await apiClient.get('/api/v2/stream/status');
            // Return the entire response object, which contains both `last_updated_token` and `update_counts`.
            return response.data;
        } catch (error) {
            console.warn('[V2DataService] Status poll failed:', error);
            return null;
        }
    }
}

export const v2DataService = new V2DataService();